import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Navbar, Button, Badge, Card, ListItem, SectionHeader, ReservationSummary,
  GuestInfoSection, OptionsSection, ShoppingSection, RouteInfoSection, PaymentInfoSection, Spinner, Icon,
} from '../../../components/common';
import VehicleInfoSection from '../../../components/service/VehicleInfoSection';
import { makeServiceLabels, makeDirectionLabels, makeOptionNameLabels, filterSelectedOptions } from '../../../constants/serviceLabels';
import SERVICE_ICON_MAP from '../../../constants/serviceIconMap';
import { reservationService, pricingService, paymentService } from '../../../api/services';
import { formatPrice } from '../../../utils/formatPrice';
import { useMoney } from '../../../hooks/useMoney';
import { useFxRate } from '../../../contexts';
import { translateApiError } from '../../../utils/apiError';

export default function TransferComplete() {
  const { t, i18n } = useTranslation();
  const money = useMoney();
  const { rate } = useFxRate();
  const lang = (i18n.language || 'ko').split('-')[0];
  const showUsd = lang !== 'ko' && rate;
  const serviceLabels = makeServiceLabels(t);
  const directionLabels = makeDirectionLabels(t);
  const optionNameLabels = makeOptionNameLabels(t);
  const { type } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isWaitingMode = searchParams.get('waiting') === 'true';

  const stateReservationId = location.state?.reservationId;
  const vbankInfo = location.state?.vbank || null;
  const [data, setData] = useState(location.state?.booking || null);
  const [loading, setLoading] = useState(!data);
  const [error, setError] = useState(null);
  const [vehicleData, setVehicleData] = useState({});
  const [copiedFlash, setCopiedFlash] = useState(false);
  const [payFailed, setPayFailed] = useState(false);

  const handleCopyReservationNo = () => {
    const num = data?.reservation_number;
    if (!num) return;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(num).catch(() => {});
    }
    setCopiedFlash(true);
    setTimeout(() => setCopiedFlash(false), 2000);
  };

  useEffect(() => {
    if (data) return;
    // 모바일 결제 redirect 복귀: 실패/취소 시 PortOne 이 code 파라미터를 붙임 → 완료 페이지로 렌더하지 않음
    //   (취소했는데 '결제 완료' 오표시 방지). isWaitingMode(대기 등록)는 결제 무관이라 제외.
    if (!isWaitingMode && searchParams.get('code')) { setPayFailed(true); setLoading(false); return; }
    const reservationId = stateReservationId || searchParams.get('id');
    if (!reservationId) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        let res = await reservationService.getById(reservationId);
        // 모바일 redirect 복귀: 결제 성공인데 아직 Payment 미생성(웹훅 미반영)이면 confirm 으로
        //   확정 처리(예약 confirmed + Payment 생성 → 결제수단·일시 표기). 이미 있으면 skip(중복 방지).
        const paymentId = searchParams.get('paymentId');
        const failCode = searchParams.get('code');
        if (paymentId && !failCode && !res.payment) {
          try {
            await paymentService.confirm({
              order_id: paymentId,
              payment_key: searchParams.get('transactionId') || searchParams.get('txId') || undefined,
              amount: res.total_price,
            });
            res = await reservationService.getById(reservationId);
          } catch (_) { /* 웹훅이 처리하므로 confirm 실패해도 무시 */ }
        }
        if (!cancelled) setData(res);
      } catch (err) {
        if (!cancelled) setError(translateApiError(err, t, 'errors.generic'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [data, stateReservationId, searchParams, t]);

  useEffect(() => {
    pricingService.getVehicleInfo(type || 'airport').then(setVehicleData).catch((e) => console.warn(e.message));
  }, [type]);

  if (payFailed) return (
    <><Navbar />
      <div className="bg-mono-50 min-h-screen flex flex-col items-center justify-center gap-5 px-5 text-center">
        <p className="text-base font-medium text-mono-700 break-keep">{t('payment.alert_payment_cancelled')}</p>
        {/* 돌아가기 → 결제 페이지로 복귀(입력값은 sessionStorage 캐시로 복원, 결제 재시도 가능) */}
        <Button level="level1" width="fixed-140" onClick={() => navigate(`/transfer/${type}/payment`)}>{t('common.go_back')}</Button>
      </div>
    </>
  );
  if (loading) return <><Navbar /><div className="flex justify-center py-8"><Spinner /></div></>;
  if (error) return <><Navbar /><p className="text-sm text-red-500 text-center py-8">{error}</p></>;
  if (!data) return <><Navbar /><p className="text-sm text-mono-400 text-center py-8">{t('complete.no_reservation_data')}</p></>;

  const isAirport = type === 'airport';
  const v = vehicleData;

  // API 응답 필드 매핑
  const route = data.route || {};
  const routeDirection = route.direction?.replace(/_/g, '-') || data.direction || '';
  // 주소 + 상세 메모를 함께 표기 (예: "서울 강남구 삼성로 212" + "은마아파트 3동")
  const departureMemo = route.departure?.memo || data.departure_detail || '';
  const destinationMemo = route.destination?.memo || data.destination_detail || '';
  const departureBase = route.departure?.address || data.departure || '';
  const arrivalBase = route.destination?.address || data.arrival || '';
  const departure = departureMemo ? `${departureBase} ${departureMemo}` : departureBase;
  const arrival = destinationMemo ? `${arrivalBase} ${destinationMemo}` : arrivalBase;
  const departureTime = route.departure?.time || route.reservation_date || data.departure_time || data.scheduled_date || '';
  const bookerName = data.booker_name || data.customer_name || '';
  const bookerPhone = data.booker_phone || data.phone || '';
  const bookerEmail = data.booker_email || data.email || '';
  const memo = data.memo || '';

  const arrivalIcon = (type === 'golf' || type === 'ski') ? 'place' : (routeDirection.startsWith('from-') ? 'place' : (SERVICE_ICON_MAP[type] || 'place'));

  const options = filterSelectedOptions(data.selected_options || data.options || []);
  const shoppingProducts = data.shopping?.items || data.shopping_products || [];
  const paymentInfo = data.payment || {};
  const totalPrice = data.total_price ?? paymentInfo.amount ?? 0;

  return (
    <>
      <Navbar />
      <main className="bg-mono-50 min-h-screen pb-28">
        <div className="max-w-form mx-auto px-2.5 sm:px-5 py-5 flex flex-col gap-1 sm:gap-5">

          {/* ===== Header ===== */}
          <div className="text-center space-y-2 py-4">
            <h1 className="text-xl font-bold text-orange-500">
              {isWaitingMode ? t('complete.waiting_complete_title') : t('complete.reservation_complete_title')}
            </h1>
            <p className="text-2sm font-normal text-mono-500 break-keep">
              {isWaitingMode
                ? t('complete.waiting_complete_desc')
                : (vbankInfo ? t('complete.vbank_complete_desc') : t('complete.reservation_complete_desc'))}
            </p>
          </div>

          {/* ===== #17 Flow A: 비회원 예약 안내 — 이메일 발송 + 예약번호 복사 =====
              결제 완료 직후 사용자가 등록 메일/예약번호로 비회원 예약 조회 플로우에
              접근할 수 있도록 안내 + 클립보드 복사 도와줌 */}
          {!isWaitingMode && data?.reservation_number && (
            <Card>
              <div className="flex flex-col gap-3">
                {bookerEmail && (
                  <div className="flex items-start gap-2.5">
                    <Icon name="mail" size={18} className="text-orange-500 mt-0.5 shrink-0" />
                    <div className="flex flex-col gap-0.5 min-w-0">
                      <p className="text-2sm font-medium text-mono-900 break-keep">
                        {t('complete.email_sent_to', { email: bookerEmail })}
                      </p>
                      <p className="text-2xs text-mono-500 break-keep">
                        {t('complete.email_sent_check_spam')}
                      </p>
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  onClick={handleCopyReservationNo}
                  className={`flex items-center justify-between gap-3 px-4 py-3 rounded-lg border transition ${
                    copiedFlash ? 'bg-orange-50 border-orange-300' : 'bg-mono-50 border-mono-100 hover:border-orange-300'
                  }`}
                >
                  <div className="flex flex-col items-start gap-0.5 min-w-0">
                    <span className="text-2xs text-mono-500">{t('reservation.reservation_no')}</span>
                    <span className="text-sm font-bold text-mono-900 font-mono">{data.reservation_number}</span>
                  </div>
                  <span className={`flex items-center gap-1 text-2sm font-semibold shrink-0 ${
                    copiedFlash ? 'text-orange-500' : 'text-mono-700'
                  }`}>
                    <Icon name={copiedFlash ? 'check' : 'copy'} size={16} />
                    {copiedFlash ? t('complete.reservation_number_copied') : t('complete.copy_reservation_number')}
                  </span>
                </button>
                {/* 로그인 회원에게는 비회원 조회 안내 숨김(본인 계정 예약내역에서 확인 가능) */}
                {!localStorage.getItem('accessToken') && (
                  <p className="text-2xs text-mono-400 break-keep">
                    {t('complete.reservation_number_copy_hint')}
                  </p>
                )}
              </div>
            </Card>
          )}

          {/* ===== Badges + meta ===== */}
          <Card>
            <ReservationSummary
              service={type === 'tour' ? t('nav.private_tour') : type === 'shopping' ? t('nav.private_shopping') : t('nav.private_transfer')}
              serviceType={type === 'tour' ? 'tour' : type === 'shopping' ? 'shopping' : 'transfer'}
              status={vbankInfo ? t('complete.vbank_status') : ({ pending: t('reservation.pending'), confirmed: t('reservation.confirmed'), completed: t('reservation.completed'), cancelled: t('reservation.cancelled'), refunded: t('reservation.refunded') }[data.status] || data.status || t('reservation.confirmed'))}
              paymentDate={data.created_at ? `${new Date(data.created_at).toLocaleDateString('ko-KR')} ${t('complete.reservation_paid')}` : ''}
              title={serviceLabels[type] || serviceLabels.airport}
              reservationNo={data.reservation_number}
              scheduledDate={route.reservation_date || departureTime}
            />
          </Card>

          {/* ===== 차량 정보 ===== */}
          <Card>
            <SectionHeader variant="card" title={t('complete.vehicle_info')} />
            <VehicleInfoSection
              images={v.images}
              carName={data.vehicle?.name || v.carName}
              usageTimeBadge={v.usageTimeBadge}
              specs={[
                { icon: 'van', text: v.seat },
                { icon: 'person', text: v.capacity },
                { icon: 'bag', text: v.luggage },
              ]}
              waitInfo={v.waitInfo}
              /* #110: 야간/주말 할증 표기 제거 — 시스템 가격 계산엔 그대로 포함됨 */
              showReview={false}
              showPriceNote={false}
            />
          </Card>

          {/* ===== 예약자 정보 ===== */}
          <Card>
            <GuestInfoSection
              name={bookerName}
              phone={bookerPhone}
              email={bookerEmail}
            />
          </Card>

          {/* ===== 반려동물 정보 (pet 서비스 전용) ===== */}
          {type === 'pet' && (() => {
            let info = { petCount: 1, petNotes: '' };
            try {
              const raw = sessionStorage.getItem('pet_booking_info');
              if (raw) info = { ...info, ...JSON.parse(raw) };
            } catch (_) {}
            return (
              <Card>
                <SectionHeader variant="card" title={t('booking.pet_info_title')} />
                <div className="flex items-start gap-4">
                  <span className="text-2sm font-medium text-mono-500 w-20 shrink-0">{t('booking.pet_count_label')}</span>
                  <div className="flex flex-col gap-1 flex-1 min-w-0">
                    <span className="text-2sm font-medium text-mono-900">{info.petCount}{t('booking.pet_unit')}</span>
                    {info.petNotes && (
                      <span className="text-2sm font-medium text-mono-500 whitespace-pre-wrap break-words">{info.petNotes}</span>
                    )}
                  </div>
                </div>
              </Card>
            );
          })()}

          {/* ===== 선택 옵션 ===== */}
          {options.length > 0 && (
            <Card>
              <OptionsSection options={options.map(opt => ({
                label: optionNameLabels[opt.code] || optionNameLabels[opt.name] || opt.name || opt.label,
                value: opt.quantity > 1 ? t('common.items_count', { n: opt.quantity }) : (opt.value || t('complete.select')),
                price: Number(opt.total_price || opt.price || 0) > 0 ? `(+${money(Number(opt.total_price || opt.price))})` : null,
              }))} />
            </Card>
          )}

          {/* ===== 프라이빗 쇼핑 서비스 ===== */}
          {isAirport && shoppingProducts.length > 0 && (
            <Card>
              <ShoppingSection
                items={shoppingProducts.map(p => ({
                  brand: p.brand,
                  name: p.name,
                  price: `${money(p.price)}`,
                  qty: p.qty,
                  image_url: p.image_url,
                }))}
                total={money(data.shopping_subtotal || 0)}
              />
            </Card>
          )}

          {/* ===== 이동 정보 ===== */}
          {/* BugHerd #65: stopovers / via 도 함께 전달 — 기존엔 비어있어 경유지 누락 노출 */}
          {(() => {
            const allStopovers = route.stopovers || [];
            const venueLabel = type === 'golf' ? t('form.golf_course') : type === 'ski' ? t('form.ski_resort') : null;
            const venueAddr = allStopovers[0]?.address || data.venue_name || data.venue_address;
            const stopoverList = venueLabel && allStopovers.length > 0 ? allStopovers.slice(1) : allStopovers;
            return (
              <Card>
                <RouteInfoSection
                  directionLabel={directionLabels[routeDirection] || directionLabels[type] || directionLabels.airport}
                  from={{ label: t('form.departure'), address: departureBase, detail: departureMemo, time: departureTime, notes: memo, icon: (routeDirection.startsWith('from-') && type !== 'airport') ? (SERVICE_ICON_MAP[type] || 'van') : 'van' }}
                  to={{ label: routeDirection.startsWith('from-') ? t('form.destination') : (type === 'airport' ? t('complete.destination_airport') : type === 'hospital' ? t('complete.destination_hospital') : t('form.destination')), address: arrivalBase, detail: destinationMemo }}
                  via={venueLabel && venueAddr ? { label: venueLabel, address: venueAddr, icon: SERVICE_ICON_MAP[type] || 'place' } : undefined}
                  stopovers={stopoverList}
                  arrivalIcon={arrivalIcon}
                />
              </Card>
            );
          })()}

          {/* ===== 결제 정보 (대기 등록 시 숨김) ===== */}
          {/* 결제 통화 + 사용자 언어 조합으로 표시 통화 결정:
                · USD 결제(PayPal) → 항상 USD 표시
                · KRW 결제 + 한국어 → KRW
                · KRW 결제 + 비한국어 + 환율 OK → USD 환산 표시 (실 결제 금액은 KRW 유지) */}
          {!isWaitingMode && (() => {
            const _cur = (paymentInfo.currency || 'KRW').toUpperCase();
            const _isUsd = _cur === 'USD';
            const _shouldShowUsd = _isUsd || showUsd;
            const _displayCur = _shouldShowUsd ? 'USD' : 'KRW';
            const _amountRaw = paymentInfo.amount != null ? Number(paymentInfo.amount) : null;
            const _totalRaw = totalPrice != null ? Number(totalPrice) : null;
            // amount 는 PaymentInfoSection 에 통화 포함 완성 문자열로 전달.
            const _fmtAmount = (n) => {
              if (_isUsd) return `$${n.toFixed(2)} USD`;
              return money(n); // KRW: money() 가 언어별로 "139,000원" 또는 "US$101" 반환
            };
            // totalAmount 는 숫자만 — 단위는 currency prop 으로 PaymentInfoSection 이 부착.
            const _fmtTotal = (n) => {
              if (_isUsd) return n.toFixed(2);
              if (showUsd) return Math.round(n / rate).toLocaleString('en-US');
              return formatPrice(n);
            };
            return (
              <Card>
                <PaymentInfoSection
                  date={paymentInfo.paid_at?.slice(0, 16)?.replace('T', ' ') || paymentInfo.date || (data.created_at ? new Date(data.created_at).toLocaleDateString('ko-KR') : '')}
                  method={vbankInfo ? t('complete.vbank_pending') : (paymentInfo.card_name || paymentInfo.method || '')}
                  amount={_amountRaw != null ? _fmtAmount(_amountRaw) : (_totalRaw != null ? _fmtAmount(_totalRaw) : '')}
                  totalAmount={_totalRaw != null ? _fmtTotal(_totalRaw) : ''}
                  currency={_displayCur}
                />
              </Card>
            );
          })()}

          {/* ===== 대기 등록 안내 ===== */}
          {isWaitingMode && (
            <Card>
              <div className="text-center py-4 space-y-2">
                <p className="text-sm font-bold text-mono-900">{t('complete.waiting_notice_title')}</p>
                <p className="text-2sm text-mono-500 break-keep">{t('complete.waiting_notice_desc')}</p>
              </div>
            </Card>
          )}

          {/* ===== 무통장입금 가상계좌 안내 ===== */}
          {vbankInfo && (
            <Card>
              <SectionHeader variant="card" title={t('complete.vbank_info')} />
              <div className="space-y-2">
                <ListItem label={t('complete.vbank_bank')} value={vbankInfo.vbank_name} layout="inline" />
                <ListItem label={t('complete.vbank_num')} value={vbankInfo.vbank_num} layout="inline" />
                <ListItem label={t('complete.vbank_holder')} value={vbankInfo.vbank_holder} layout="inline" />
                {vbankInfo.vbank_date && (
                  <ListItem label={t('complete.vbank_date')} value={new Date(vbankInfo.vbank_date * 1000).toLocaleString('ko-KR')} layout="inline" />
                )}
              </div>
              <p className="text-2sm font-medium text-orange-500 mt-2 break-keep">{t('complete.vbank_notice')}</p>
            </Card>
          )}

          {/* ===== Bottom action ===== */}
          <Button level="level2" fullWidth onClick={() => navigate(data.id ? `/reservation/${data.id}` : '/reservation')}>
            {isWaitingMode ? t('complete.view_waiting') : t('complete.view_reservation')}
          </Button>

        </div>
      </main>
    </>
  );
}
